import React, {useContext, useEffect, useRef, useState} from 'react';
import StatusChip, {SmallStatusChip} from "../../components/StatusChip";
import Box from "@mui/material/Box";
import {
    formatDurationBrief, formatDurationSidebar, FormatTimeRange, formatToHumanTime,
    formatToReadableLargeTitle,
    formatUnixTime, formatUnixTimeBrief,
    FormatUnixTimeForSchedule, LibrettoProjectTypes
} from "../../utils/utils";
import {CheckBoxOutlineBlankOutlined, NoteOutlined} from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {Typography, Dialog, DialogContent, TextField, Button, Card, ClickAwayListener} from "@mui/material";
import Popper from "@mui/material/Popper";
import dayjs from "dayjs";
import {ProjectCardThreeDotsButton, ThreeDotsButton} from "./HomeButtons";
import './ProjectCard.css';
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {DeleteIcon, RenameIcon} from "./HomeIcons";
import {MyDeleteIcon, NewDeleteIcon} from "./NewAssetAndProjectCards";
import {ScheduledProjectThumbnail} from "./ScheduledProjectThumbnail";
import DeleteProjectButton from "./DeleteProjectButton";
import {FetchContext} from "../../context/FetchContext";
import {AuthContext} from "../../context/AuthContext";
import {useAssetStore} from "../../storage/AssetStore";
import {doc, updateDoc} from "firebase/firestore";
import {db} from "../../Firebase";
import {kProjectsCollectionName, ProjectNameSource} from "../../utils/CollectionConstants";

// Session-scoped aspect ratio cache to avoid layout jumps on re-render
const ThumbnailAspectCache = new Map(); // key: thumbnailId -> 'landscape' | 'portrait' | 'square'

export const ProjectContextMenu = ({
                                       setThreeDotsMenuOpen,
                                       top,
                                       right,
                                       handleRenameClick,
                                       handleDeleteClick,
                                       isAsset
                                   }) => {
    const boxStyle = {
        position: "absolute",
        top: top || "70px",
        right: right || (isAsset ? "260px" : "515px"),
        display: "inline-flex",
        padding: "16px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "4px",
        borderRadius: "12px",
        background: "var(--app-bg-surface)",
        border: "1px solid var(--app-border-subtle)",
        boxShadow: "0px 30px 80px 0px rgba(0, 0, 0, 0.15)",
        zIndex: 1000,
    };

    const itemStyle = {
        display: "flex",
        padding: "16px",
        alignItems: "center",
        gap: "8px",
        alignSelf: "stretch",
        cursor: "pointer",
        borderRadius: "8px",
        '&:hover': {
            background: "var(--app-bg-subtle)",
        },
    };

    const itemTextStyle = {
        fontFamily: "Inter",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "20px",
        letterSpacing: "0.14px",
        color: "var(--app-text-primary)",
    };

    return (
        <ClickAwayListener onClickAway={() => setThreeDotsMenuOpen(false)}>
            <Card style={boxStyle}>
                <Box sx={itemStyle} onClick={handleRenameClick}>
                    <Box style={{display: "flex", height: "20px", width: "20px"}}>
                        <RenameIcon/>
                    </Box>
                    <Box style={itemTextStyle}>
                        {isAsset ? "Rename Recording" : "Rename Project"}
                    </Box>
                </Box>
                <Box sx={itemStyle} onClick={handleDeleteClick}>
                    <Box style={{display: "flex", height: "20px", width: "20px"}}>
                        <DeleteIcon/>
                    </Box>
                    <Box style={itemTextStyle}>
                        {isAsset ? "Delete Recording" : "Delete Project"}
                    </Box>
                </Box>
            </Card>
        </ClickAwayListener>
    );
};

export const AudioWaveformIcon = (backgroundColor) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="124" height="93" viewBox="0 0 124 93" fill="none">
            {/* Audio waveform SVG code */}
            <rect width="124" height="93" rx="6" fill={backgroundColor}/>
            <line x1="69.5" y1="36.333" x2="69.5" y2="57.6663" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="87.5" y1="44.7139" x2="87.5" y2="49.2853" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="57.5" y1="41.667" x2="57.5" y2="52.3337" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="81.5" y1="40.1426" x2="81.5" y2="53.8569" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="51.5" y1="35.5713" x2="51.5" y2="58.4284" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="63.5" y1="40.1426" x2="63.5" y2="53.8569" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="45.5" y1="43.1904" x2="45.5" y2="50.8095" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="75.5" y1="31" x2="75.5" y2="63" stroke="var(--app-icon-primary)" strokeWidth="2"/>
            <line x1="39.5" y1="44.7139" x2="39.5" y2="49.2853" stroke="var(--app-icon-primary)" strokeWidth="2"/>
        </svg>
    )
}

// Small version for compact list view (72x40)
export const SmallAudioWaveformIcon = ({backgroundColor = "var(--app-bg-subtle)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="72" height="40" viewBox="0 0 72 40" fill="none">
            <rect width="72" height="40" rx="6" fill={backgroundColor}/>
            <line x1="24" y1="17" x2="24" y2="23" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="28" y1="14" x2="28" y2="26" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="32" y1="12" x2="32" y2="28" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="36" y1="15" x2="36" y2="25" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="40" y1="13" x2="40" y2="27" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="44" y1="16" x2="44" y2="24" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="48" y1="18" x2="48" y2="22" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
    )
}

export const VideoCameraIcon = ({backgroundColor}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="124" height="93" viewBox="0 0 124 93" fill="none">
            <rect width="124" height="93" rx="6" fill={backgroundColor}/>
            <rect x="30" y="30" width="44" height="33" rx="3" stroke="var(--app-icon-primary)" strokeWidth="2.5"/>
            <path d="M74 38L94 28V65L74 55" stroke="var(--app-icon-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
};

// Small version for compact list view (72x40)
export const SmallVideoCameraIcon = ({backgroundColor = "var(--app-bg-subtle)"}) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="72" height="40" viewBox="0 0 72 40" fill="none">
            <rect width="72" height="40" rx="6" fill={backgroundColor}/>
            <rect x="18" y="12" width="24" height="16" rx="2" stroke="var(--app-icon-primary)" strokeWidth="1.5"/>
            <path d="M42 16L52 12V28L42 24" stroke="var(--app-icon-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
};

export const ListViewThumbnailCard = ({isVideo, thumbnailId, projectId, onClick, status, title}) => {
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [isImageLoaded, setIsImageLoaded] = useState(true);

    const { GetThumbnailUrl } = useAssetStore();
    const authContext = useContext(AuthContext);

    const [thumbnailUrl, setThumbnailUrl] = useState();

    // simple in-memory cache so multiple cards don’t refetch
    const cacheRef = useRef(new Map());

    useEffect(() => {
        if (!thumbnailId) { setThumbnailUrl(undefined); return; }

        const cached = cacheRef.current.get(thumbnailId);
        if (cached) { setThumbnailUrl(cached); return; }

        let cancelled = false;
        (async () => {
            const url = await GetThumbnailUrl({ thumbnailId, projectId });
            if (cancelled) return;
            if (url) {
                cacheRef.current.set(thumbnailId, url);
                setThumbnailUrl(url);
            } else {
                setThumbnailUrl(undefined);
            }
        })();

        return () => { cancelled = true; };
    }, [thumbnailId, projectId, GetThumbnailUrl, authContext.authReady, authContext.authState?.token]);


    useEffect(() => {
        if (!isVideo || !thumbnailUrl) return;
        // Apply cached aspect immediately to avoid visual jump
        const cached = ThumbnailAspectCache.get(thumbnailId);
        if (cached) setAspectRatio(cached);
        const img = new Image();
        img.onload = () => {
            const ratio = img.width / img.height;
            const ar = ratio > 1.1 ? 'landscape' : ratio < 0.9 ? 'portrait' : 'square';
            setAspectRatio(ar);
            ThumbnailAspectCache.set(thumbnailId, ar);
        };
        img.src = thumbnailUrl;
    }, [isVideo, thumbnailUrl, thumbnailId]);

    const renderThumbnail = () => {
        if (isVideo && thumbnailUrl && isImageLoaded) {
            return <img src={thumbnailUrl} alt={title} style={getThumbnailStyle()} onError={(e) => {
                e.target.onerror = null;
                e.target.src = setIsImageLoaded(false);
            }}/>;
        } else if (isVideo) {
            return (
                <div style={styles.smallIconContainer}>
                    <SmallVideoCameraIcon backgroundColor="var(--app-bg-subtle)"/>
                </div>
            );
        } else {
            return (
                <div style={styles.smallIconContainer}>
                    <SmallAudioWaveformIcon backgroundColor="var(--app-bg-subtle)"/>
                </div>
            );
        }
    };

    const getThumbnailStyle = () => {
        const baseStyle = {
            backgroundColor: 'var(--app-bg-subtle)',
        };

        switch (aspectRatio) {
            case 'portrait':
                return {
                    ...baseStyle, position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                };
            case 'square':
                return {
                    ...baseStyle, position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                };
            case 'landscape':
            default:
                return {
                    ...baseStyle,
                    top: 0,
                    left: 0,
                    position: "absolute",
                    width: '100%',
                    height: "100%",
                    objectFit: 'cover'
                };
        }
    };

    const hoverOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        ':hover': {
            opacity: 1,
        },
    }

    return (
        <Box sx={styles.cardWrapper} onClick={onClick}>
            <Box style={styles.thumbnailContainer}>
                {renderThumbnail()}
                <div style={hoverOverlay}/>
            </Box>
        </Box>
    );
}

export const HomeSidebarButton = ({title, onClick, isSelected, startIcon}) => {

    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const width = isXl ? "250px" : isLg ? "230px" : "220px";

    const iconBoxStyle = {
        // width: "14px",
        // height: "14px",
        paddingBottom: "3px",
    }

    return (
        <Box
            onClick={onClick}
            sx={{
                width: width,
                height: "35px", // Fixed height for consistency
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                padding: '4px',
                gap: "10px",
                paddingLeft: '4px',
                borderRadius: "5px",
                cursor: 'pointer',
                marginBottom: '10px',
                transition: 'all 0.2s ease-in-out',
                background: isSelected ? 'rgba(43, 107, 253, 0.1)' : 'linear-gradient(135deg, #f8f9fb 0%, #eef1f6 100%)',
                // border: isSelected ? '1px solid #2B6BFD' : '1px solid #E0E0E0',
                '&:hover': {
                    background: 'rgba(43, 107, 253, 0.08)',
                    boxShadow: 'inset 0px 0px 4px rgba(0, 0, 0, 0.1)',
                }
            }}
        >
            <Box sx={iconBoxStyle}>
                {startIcon}
            </Box>
            <Box
                sx={{
                    fontSize: "17px",
                    fontWeight: isSelected ? 400 : 350,
                    color: isSelected ? "var(--app-text-primary)" : "var(--app-text-muted)",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {title}
            </Box>

            {/* Metadata Row */}
            {/*<Box sx={{ display: "flex", justifyContent: "center", gap: 2, fontSize: "13px", color: "#6D6D6D" }}>*/}
            {/*    <Box>*/}
            {/*        {numRecordings > 0 ? `${numRecordings} recording${numRecordings > 1 ? "s" : ""}` : "No recordings"}*/}
            {/*    </Box>*/}
            {/*    <Box>*/}
            {/*        {numDrafts > 0 ? `${numDrafts} edit${numDrafts > 1 ? "s" : ""}` : "No edits"}*/}
            {/*    </Box>*/}
            {/*</Box>*/}

            {/*/!* Date & Duration *!/*/}
            {/*<Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "4px", fontSize: "12px", color: "#6D6D6D" }}>*/}
            {/*    <Box>{formatUnixTime(createTime)}</Box>*/}
            {/*    <Box style={styles.newSidebarDate}>*/}
            {/*        {totalDuration && numRecordings > 0 ? (*/}
            {/*            <div>{formatDurationSidebar(totalDuration)}</div>*/}
            {/*        ) : null}*/}
            {/*    </Box>*/}
            {/*</Box>*/}
        </Box>
    );
}

export const RedesignedSidebarThumbnailCard = ({
                                                   title,
                                                   createTime,
                                                   projectId,
                                                   currentProjectId,
                                                   totalDuration,
                                                   numRecordings,
                                                   numDrafts,
                                                   onClick
                                               }) => {
    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const width = isXl ? "250px" : isLg ? "230px" : "220px";

    const isSelected = projectId === currentProjectId;

    return (
        <Box
            onClick={onClick}
            sx={{
                width: width,
                height: "85px", // Fixed height for consistency
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '8px',
                borderRadius: "10px",
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                background: isSelected ? 'rgba(43, 107, 253, 0.1)' : 'linear-gradient(135deg, #f8f9fb 0%, #eef1f6 100%)',
                border: isSelected ? '1px solid #2B6BFD' : '1px solid #E0E0E0',
                '&:hover': {
                    background: 'rgba(43, 107, 253, 0.08)',
                    boxShadow: 'inset 0px 0px 4px rgba(0, 0, 0, 0.1)',
                }
            }}
        >
            {/* Title */}
            <Typography
                sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "var(--app-text-primary)",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {title}
            </Typography>

            {/* Metadata Row */}
            <Box sx={{display: "flex", justifyContent: "center", gap: 2, fontSize: "13px", color: "#6D6D6D"}}>
                <Box>
                    {numRecordings > 0 ? `${numRecordings} recording${numRecordings > 1 ? "s" : ""}` : "No recordings"}
                </Box>
                <Box>
                    {numDrafts > 0 ? `${numDrafts} edit${numDrafts > 1 ? "s" : ""}` : "No edits"}
                </Box>
            </Box>

            {/* Date & Duration */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "4px",
                fontSize: "12px",
                color: "#6D6D6D"
            }}>
                <Box>{formatUnixTime(createTime)}</Box>
                <Box style={styles.newSidebarDate}>
                    {totalDuration && numRecordings > 0 ? (
                        <div>{formatDurationSidebar(totalDuration)}</div>
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
};

export const NewSidebarThumbnailCard = ({
                                            title,
                                            createTime,
                                            projectId,
                                            currentProjectId,
                                            totalDuration,
                                            numRecordings,
                                            numDrafts,
                                            thumbnailUrl,
                                            onClick
                                        }) => {

    const theme = useTheme();

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const width = isXl ? "250px" : isLg ? "230px" : "220px";

    const infoContainerWidth = isXl ? "240px" : isLg ? "220px" : "200px";

    const fixedInfoContainer = (width) => {
        return ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '2px',
            width: infoContainerWidth,
        })
    }

    const sidebarCardWrapper = (width) => {
        return ({
            width: '100%',
            maxWidth: width,
            cursor: 'pointer',
            padding: '4px',
            border: '1px solid transparent',
            borderRadius: "8px",
            '&:hover': {
                background: 'rgba(43, 107, 253, 0.05);',
                cursor: 'pointer',
            }
        })
    }

    const selectedCardWrapper = (width) => {
        return ({
            width: '100%',
            maxWidth: width,
            padding: '4px',
            cursor: 'pointer',
            border: '1px solid #2B6BFD',
            borderRadius: "8px",
            '&:hover': {
                background: 'rgba(43, 107, 253, 0.05);',
                cursor: 'pointer',
            }
        })
    }

    const recordingsText = numRecordings > 1 ? "recordings" : "recording";

    const draftsText = numDrafts > 1 ? "edits" : "edit";

    const isSelected = projectId === currentProjectId;

    const renderThumbnail = () => {
        return (
            <div style={styles.projectIconContainer}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "18px",
                            color: "var(--app-text-primary)",
                            fontWeight: 500,
                            marginBottom: '4px', // Add spacing between title and metadata
                        }}
                    >
                        {formatToReadableLargeTitle(title)}
                    </Typography>
                    <Box sx={{color: "var(--app-text-muted)", fontSize: "14px"}}>
                        {numRecordings > 0 ? `${numRecordings} ${recordingsText}` : "No recordings"}
                    </Box>
                    <Box sx={{color: "var(--app-text-muted)", fontSize: "14px"}}>
                        {numDrafts > 0 ? ` ${numDrafts} ${draftsText}` : "No edits"}
                    </Box>
                </Box>
            </div>
        )
    };

    const hoverOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        ':hover': {
            opacity: 1,
        },
    }

    if (isSelected) {
        return (
            <Box sx={selectedCardWrapper(width)} onClick={onClick}>
                <Box style={styles.sidebarThumbnailContainer}>
                    {renderThumbnail()}
                    <div style={hoverOverlay}/>
                </Box>
                <Box style={fixedInfoContainer(width)}>
                    <Box style={styles.titleContainer}>
                        <Box style={styles.newSidebarDate}>{formatUnixTime(createTime)}</Box>
                    </Box>
                    <Box style={styles.newSidebarDate}>
                        {totalDuration && numRecordings > 0 ? (
                            <div>{formatDurationSidebar(totalDuration)}</div>
                        ) : null}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={sidebarCardWrapper(width)} onClick={onClick}>
            <Box style={styles.sidebarThumbnailContainer}>
                {renderThumbnail()}
                <div style={hoverOverlay}/>
            </Box>
            <Box style={fixedInfoContainer(width)}>
                <Box style={styles.titleContainer}>
                    <Box style={styles.newSidebarDate}>{formatUnixTime(createTime)}</Box>
                </Box>
                <Box style={styles.newSidebarDate}>
                    {totalDuration && numRecordings > 0 ? (
                        <div>{formatDurationSidebar(totalDuration)}</div>
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
};

export const SidebarProjectThumbnail = ({
                                            title,
                                            createTime,
                                            projectId,
                                            currentProjectId,
                                            totalDuration,
                                            numRecordings,
                                            numDrafts,
                                            thumbnailUrl,
                                            onClick
                                        }) => {
    const [aspectRatio, setAspectRatio] = useState('16:9');

    const isSelected = projectId === currentProjectId;

    useEffect(() => {
        if (thumbnailUrl) {
            const img = new Image();
            img.onload = () => {
                const ratio = img.width / img.height;
                if (ratio > 1.1) setAspectRatio('landscape');
                else if (ratio < 0.9) setAspectRatio('portrait');
                else setAspectRatio('square');
            };
            img.src = thumbnailUrl;
        }
    }, [thumbnailUrl]);

    const renderThumbnail = () => {
        if (thumbnailUrl) {
            return <img src={thumbnailUrl} alt={title} style={getThumbnailStyle()}/>;
        } else if (numRecordings > 0) {
            return (
                <div style={styles.iconContainer}>
                    <AudioWaveformIcon backgroundColor="var(--app-bg-subtle)"/>
                </div>
            );
        } else {
            return (
                <div style={styles.iconContainer}>
                    <CheckBoxOutlineBlankOutlined backgroundColor="var(--app-bg-subtle)"/>
                </div>
            );
        }
    };

    const getThumbnailStyle = () => {
        const baseStyle = {
            backgroundColor: 'var(--app-bg-subtle)',
        };

        switch (aspectRatio) {
            case 'portrait':
                return {
                    ...baseStyle, position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                };
            case 'square':
                return {
                    ...baseStyle, position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                };
            case 'landscape':
            default:
                return {
                    ...baseStyle,
                    top: 0,
                    left: 0,
                    position: "absolute",
                    width: '100%',
                    height: "100%",
                    objectFit: 'cover'
                };
        }
    };

    const hoverOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        ':hover': {
            opacity: 1,
        },
    }

    const theme = useTheme();

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const width = isXl ? "250px" : isLg ? "230px" : "220px";

    const infoContainerWidth = isXl ? "240px" : isLg ? "220px" : "200px";

    const fixedInfoContainer = (width) => {
        return ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '8px',
            width: infoContainerWidth,
        })
    }

    const sidebarCardWrapper = (width) => {
        return ({
            width: '100%',
            maxWidth: width,
            cursor: 'pointer',
            padding: '4px',
            border: '1px solid transparent',
            borderRadius: "8px",
        })
    }

    const selectedCardWrapper = (width) => {
        return ({
            width: '100%',
            maxWidth: width,
            padding: '4px',
            cursor: 'pointer',
            border: '0.5px solid #2B6BFD',
            borderRadius: "8px",
        })
    }

    const recordingsText = numRecordings > 1 ? "recordings" : "recording";

    const draftsText = numDrafts > 1 ? "edits" : "edit";

    if (isSelected) {
        return (
            <Box sx={selectedCardWrapper(width)} onClick={onClick}>
                <Box style={styles.thumbnailContainer}>
                    {renderThumbnail()}
                    <div style={hoverOverlay}/>
                    {totalDuration && numRecordings > 0 ?
                        <div style={styles.durationOverlay}>{formatDurationBrief(totalDuration)}</div> : null}
                </Box>
                <Box style={fixedInfoContainer(width)}>
                    <Box style={styles.titleContainer}>
                        <Box style={styles.title}>{formatToReadableLargeTitle(title)}</Box>
                        <span style={styles.date}>{formatUnixTime(createTime)}</span>
                    </Box>
                    <Box>
                        {numRecordings > 0 ? <Box style={styles.sidebarDate}>{numRecordings} {recordingsText}</Box> :
                            <Box style={styles.sidebarDate}>No recordings</Box>}
                        {numDrafts > 0 ? <Box style={styles.sidebarDate}>{numDrafts} {draftsText}</Box> :
                            <Box style={styles.sidebarDate}>No edits</Box>}
                    </Box>

                </Box>
            </Box>
        );
    }

    return (
        <Box sx={sidebarCardWrapper(width)} onClick={onClick}>
            <Box style={styles.thumbnailContainer}>
                {renderThumbnail()}
                <div style={hoverOverlay}/>
                {totalDuration && numRecordings > 0 ?
                    <div style={styles.durationOverlay}>{formatDurationBrief(totalDuration)}</div> : null}
            </Box>
            <Box style={fixedInfoContainer(width)}>
                <Box style={styles.titleContainer}>
                    <Box style={styles.title}>{formatToReadableLargeTitle(title)}</Box>
                    <span style={styles.date}>{formatUnixTime(createTime)}</span>
                </Box>
                <Box>
                    {numRecordings > 0 ? <Box style={styles.sidebarDate}>{numRecordings} {recordingsText}</Box> :
                        <Box style={styles.sidebarDate}>No recordings</Box>}
                    {numDrafts > 0 ? <Box style={styles.sidebarDate}>{numDrafts} {draftsText}</Box> :
                        <Box style={styles.sidebarDate}>No edits</Box>}
                </Box>
            </Box>
        </Box>
    );
};

const dateStyle = {
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 400,
    fontStyle: "normal",
    lineHeight: "20px",
    letterSpacing: "0.14px",
}

const saveButtonStyle = {
    display: "flex",
    flexDirection: "row",
    height: "45px",
    width: "154px",
    padding: "10px 20px",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    borderRadius: "6px",
    backgroundColor: "#2B6BFD",
    cursor: "pointer",
    // Set hovered state
    "&:hover": {
        background: "linear-gradient(0deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.15) 100%), #2B6BFD",
    },
    // Set focused state
    "&:focus": {
        background: "linear-gradient(0deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), #2B6BFD"
    },
    // Set disabled state
    "&:disabled": {
        background: "var(--app-border-muted)",
        opacity: 1,
        cursor: "not-allowed",
    }
}

const textStyle = {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "medium",
    lineHeight: "27px",
    letterSpacing: '-0.5px',
}

const whiteTextStyle = {
    ...textStyle,
    color: "#FFFFFF",
}

export const ProjectGridCard = ({
                                    title,
                                    createTime,
                                    lastViewed,
                                    isVideo,
                                    isEmpty,
                                    duration,
                                    projectId,
                                    thumbnailId,
                                    scheduledTimezone,
                                    scheduledStartTime,
                                    scheduledEndTime,
                                    onClick,
                                    progress,
                                    status,
                                    onDeleteClick,
                                    projectType
                                }) => {

    const [thumbnailUrl, setThumbnailUrl] = useState();

    // simple in-memory cache so multiple cards don’t refetch
    const cacheRef = useRef(new Map());

    const { GetThumbnailUrl } = useAssetStore();
    const authContext = useContext(AuthContext);

    useEffect(() => {
        if (!thumbnailId) { setThumbnailUrl(undefined); return; }

        const cached = cacheRef.current.get(thumbnailId);
        if (cached) { setThumbnailUrl(cached); return; }

        let cancelled = false;
        (async () => {
            const url = await GetThumbnailUrl({ thumbnailId, projectId });
            if (cancelled) return;
            if (url) {
                cacheRef.current.set(thumbnailId, url);
                setThumbnailUrl(url);
            } else {
                setThumbnailUrl(undefined);
            }
        })();

        return () => { cancelled = true; };
    }, [thumbnailId, projectId, GetThumbnailUrl, authContext.authReady, authContext.authState?.token]);



    const handleDeleteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onDeleteClick();
    }

    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState(title ?? "");
    const [isImageLoaded, setIsImageLoaded] = useState(true);

    // Avoid dynamic aspect switching in grid to prevent layout flash; keep consistent cover style
    // Intentionally no onload aspect adjustments here.


    if (projectType === LibrettoProjectTypes.ProjectTypeScheduled) {
        return (
            <ScheduledProjectThumbnail
                title={title}
                createTime={createTime}
                onClick={onClick}
                onDeleteClick={handleDeleteClick}
                scheduledStartTime={scheduledStartTime}
                scheduledEndTime={scheduledEndTime}
                scheduledTimezone={scheduledTimezone}
            />
        );
    }

    const handleClick = () => {
        onClick();
    }

    const handleRenameConfirm = async () => {
        try {
            const newName = (renameValue || "").trim();
            if (!newName) return;
            const entityRef = doc(db, kProjectsCollectionName, projectId);
            await updateDoc(entityRef, {name: newName, [ProjectNameSource]: "user"});
            setRenameOpen(false);
        } catch (e) {
            console.error("Failed to rename project", e);
        }
    };


    const renderThumbnail = () => {
        if (isEmpty) {
            return <div style={styles.iconContainer}>
                <NoteOutlined/>
            </div>
        }
        else if (isVideo && thumbnailUrl && isImageLoaded) {
            return <img src={thumbnailUrl} alt={title} style={getThumbnailStyle()} onError={() => setIsImageLoaded(false)}/>;
        } else if (isVideo) {
            return (
                <div style={styles.iconContainer}>
                    <VideoCameraIcon backgroundColor="var(--app-bg-subtle)"/>
                </div>
            );
        } else {
            return (
                <div style={styles.iconContainer}>
                    <AudioWaveformIcon backgroundColor="var(--app-bg-subtle)"/>
                </div>
            );
        }
    };

    const getThumbnailStyle = () => ({
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        backgroundColor: "var(--app-bg-subtle)",
    });

    const hoverOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        ':hover': {
            opacity: 1,
        },
    }

    const usesPlaceholderThumbnail =
        isEmpty || !thumbnailUrl || !isImageLoaded || !isVideo;
    const effectiveThumbnailContainerStyle = usesPlaceholderThumbnail
        ? {
            ...styles.thumbnailContainer,
            border: "1px solid var(--app-border-subtle)",
            boxSizing: "border-box",
        }
        : styles.thumbnailContainer;

    return (
        <Box className={"project-card"} sx={styles.cardWrapper} onClick={handleClick}>
            <Box style={effectiveThumbnailContainerStyle}>
                {renderThumbnail()}
                <div style={hoverOverlay}/>
            </Box>
            <Box style={styles.infoContainer}>
                <Box style={styles.titleContainer}>
                    <Box style={styles.title}>{formatToReadableLargeTitle(title)}</Box>
                    <span
                        style={styles.date}>{lastViewed ? formatToHumanTime(lastViewed) : formatToHumanTime(createTime)}</span>
                </Box>
                <Box style={{display: "flex", flexDirection: "column", gap: "2px", alignItems: "center"}}>
                    {duration === 0 ?
                        null :
                        <Box style={styles.newSidebarDate}>{formatDurationBrief(duration)}</Box>}
                    <Box className="three-dots-button">
                        {/* Replace delete button with 3-dots context menu */}
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setMenuAnchor(e.currentTarget);
                            }}
                            sx={{ color: 'var(--app-icon-primary)' }}
                        >
                            <MoreVertIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Popper open={Boolean(menuAnchor)} anchorEl={menuAnchor} placement="bottom-end" sx={{ zIndex: 2000 }}>
                <ClickAwayListener onClickAway={() => setMenuAnchor(null)}>
                    <Card
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            display: 'inline-flex',
                            p: 1,
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            width: 200,
                            gap: '4px',
                            borderRadius: '12px',
                            background: 'var(--app-bg-surface)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid var(--app-border-subtle)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                p: 1,
                                alignItems: 'center',
                                gap: '8px',
                                alignSelf: 'stretch',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                '&:hover': { background: 'var(--app-bg-subtle)' },
                            }}
                            onClick={(e) => { e.stopPropagation(); setMenuAnchor(null); setRenameOpen(true); }}
                        >
                            <Box sx={{ display: 'flex', color: 'var(--app-icon-primary)' }}><RenameIcon/></Box>
                            <Box sx={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '20px', letterSpacing: '0.14px', color: 'var(--app-text-primary)' }}>Rename</Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                p: 1,
                                alignItems: 'center',
                                gap: '8px',
                                alignSelf: 'stretch',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                '&:hover': { background: 'rgba(237, 12, 50, 0.12)' },
                            }}
                            onClick={(e) => { e.stopPropagation(); setMenuAnchor(null); handleDeleteClick(e); }}
                        >
                            <Box sx={{ display: 'flex', color: '#C63636' }}><NewDeleteIcon/></Box>
                            <Box sx={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '20px', letterSpacing: '0.14px', color: '#C63636' }}>Delete</Box>
                        </Box>
                    </Card>
                </ClickAwayListener>
            </Popper>
            <Dialog
                open={renameOpen}
                onClose={() => setRenameOpen(false)}
                aria-labelledby="rename-project-dialog"
                onClick={(e) => e.stopPropagation()}
            >
                <DialogContent sx={{ minWidth: { xs: '280px', md: '400px' } }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Rename Project</Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void handleRenameConfirm(); }}
                        placeholder="New project name"
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            onClick={handleRenameConfirm}
                            disabled={!renameValue.trim()}
                            sx={saveButtonStyle}
                        >
                            <div style={whiteTextStyle}>Rename</div>
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const ProjectThumbnailCard = ({
                                  title,
                                  createTime,
    projectId,
                                  status,
                                  progress,
                                  isVideo,
                                  thumbnailId,
                                  onClick,
                                  onScheduledSessionClick,
                                  scheduledStartTime,
                                  scheduledEndTime,
                                  scheduledTimezone
                              }) => {
    const [aspectRatio, setAspectRatio] = useState('16:9');


    const handleClick = () => {
        if (status === "Scheduled") {
            onScheduledSessionClick();
        } else {
            onClick();
        }
    }

    const cacheRef = useRef(new Map());
    const [thumbnailUrl, setThumbnailUrl] = useState();
    const { GetThumbnailUrl } = useAssetStore();
    const authContext = useContext(AuthContext);

    useEffect(() => {
        if (!thumbnailId) { setThumbnailUrl(undefined); return; }

        const cached = cacheRef.current.get(thumbnailId);
        if (cached) { setThumbnailUrl(cached); return; }

        let cancelled = false;
        (async () => {
            const url = await GetThumbnailUrl({ thumbnailId, projectId });
            if (cancelled) return;
            if (url) {
                cacheRef.current.set(thumbnailId, url);
                setThumbnailUrl(url);
            } else {
                setThumbnailUrl(undefined);
            }
        })();

        return () => { cancelled = true; };
    }, [thumbnailId, projectId, GetThumbnailUrl, authContext.authReady, authContext.authState?.token]);


    useEffect(() => {
        if (isVideo && thumbnailUrl) {
            const img = new Image();
            img.onload = () => {
                const ratio = img.width / img.height;
                if (ratio > 1.1) setAspectRatio('landscape');
                else if (ratio < 0.9) setAspectRatio('portrait');
                else setAspectRatio('square');
            };
            img.src = thumbnailUrl;
        }
    }, [isVideo, thumbnailUrl]);

    if (status === "Processing...") {
        status = "Processing";
    }

    const renderThumbnail = () => {
        if (status === 'Ready') {
            if (isVideo && thumbnailUrl) {
                return <img src={thumbnailUrl} alt={title} style={getThumbnailStyle()}/>;
            } else if (isVideo) {
                return (
                    <div style={styles.iconContainer}>
                        <VideoCameraIcon backgroundColor="var(--app-bg-subtle)"/>
                    </div>
                );
            } else {
                return (
                    <div style={styles.iconContainer}>
                        <AudioWaveformIcon backgroundColor="var(--app-bg-subtle)"/>
                    </div>
                );
            }
        } else if (status === 'Processing') {
            return (
                <div style={styles.iconContainer}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="20" viewBox="0 0 12 20" fill="none">
                        <path opacity="0.3" d="M2 5.5L6 9.5L10 5.5V2H2V5.5Z" fill="#F2880C"/>
                        <path opacity="0.3" d="M2 5.5L6 9.5L10 5.5V2H2V5.5Z" fill="#F2880C"/>
                        <path
                            d="M12 0H0V6L4 10L0.0100002 14.01L0 20H12L11.99 14.01L8 10L12 6.01V0ZM10 14.5V18H2V14.5L6 10.5L10 14.5ZM10 5.5L6 9.5L2 5.5V2H10V5.5Z"
                            fill="#F2880C"/>
                    </svg>
                </div>
            );
        } else if (status === 'Uploading') {
            return (
                <div style={styles.iconContainer}>
                    {/* You can add an upload icon SVG here */}
                    <span>Uploading...</span>
                </div>
            );
        } else if (status === "Scheduled") {
            return (
                <div style={styles.iconContainer}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "var(--app-text-muted)",
                                fontWeight: 400,
                            }}
                        >
                            {FormatUnixTimeForSchedule(scheduledStartTime, scheduledTimezone)}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "28px",
                                fontWeight: 700,
                                color: "var(--app-text-primary)",
                                lineHeight: "32px",
                            }}
                        >
                            {dayjs.unix(scheduledStartTime).format("DD")}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "var(--app-text-muted)",
                                fontWeight: 400,
                            }}
                        >
                            {dayjs.unix(scheduledStartTime).format("MMM").toUpperCase()}
                        </Typography>
                        <Box sx={dateStyle}>
                            {FormatTimeRange(scheduledStartTime, scheduledEndTime, scheduledTimezone)}
                        </Box>
                        <Box sx={dateStyle}>
                            {scheduledTimezone}
                        </Box>
                    </Box>
                </div>
            )
        }
    };

    const getThumbnailStyle = () => {
        const baseStyle = {
            backgroundColor: 'var(--app-bg-subtle)',
        };

        switch (aspectRatio) {
            case 'portrait':
                return {
                    ...baseStyle, position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                };
            case 'square':
                return {
                    ...baseStyle, position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                };
            case 'landscape':
            default:
                return {
                    ...baseStyle,
                    top: 0,
                    left: 0,
                    position: "absolute",
                    width: '100%',
                    height: "100%",
                    objectFit: 'cover'
                };
        }
    };

    const hoverOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        ':hover': {
            opacity: 1,
        },
    }

    return (
        <Box sx={styles.cardWrapper} onClick={handleClick}>
            <Box style={styles.thumbnailContainer}>
                {renderThumbnail()}
                <div style={hoverOverlay}/>
            </Box>
            <Box style={styles.infoContainer}>
                <Box style={styles.titleContainer}>
                    <Box style={styles.title}>{formatToReadableLargeTitle(title)}</Box>
                    <span style={styles.date}>{formatUnixTime(createTime)}</span>
                </Box>
                <StatusChip status={status} progress={progress}/>
            </Box>
        </Box>
    );
};

const styles = {
    cardWrapper: {
        width: '100%',
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out',
        ':hover': {
            transform: 'scale(1.02)',
        },
    },
    thumbnailContainer: {
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        position: 'relative',
        backgroundColor: 'var(--app-bg-subtle)',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '8px',
    },
    sidebarThumbnailContainer: {
        width: '100%',
        paddingTop: '56.25%',
        position: 'relative',
        backgroundColor: 'var(--app-bg-subtle)',
        borderRadius: '8px',
        boxShadow: '1px 1px 1px 1px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        marginBottom: '8px',
        transition: 'box-shadow 0.2s ease', // Smooth transition
        '&:hover': {
            boxShadow: '1p 1px 1px 1px rgba(0, 0, 0, 0.55)', // Slightly stronger shadow on hover
        },
    },
    durationOverlay: {
        position: 'absolute',
        bottom: 3,
        right: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '10px',
        fontWeight: 400,
        letterSpacing: '1%',
        lineHeight: '10px',
    },
    thumbnailImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    projectIconContainer: {
        position: 'absolute',
        top: '30%',
        left: '10%',
        transform: 'translate(-10%, -30%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    iconContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    smallIconContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    infoContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    titleContainer: {
        flex: 1,
        marginRight: '8px',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '14px',
        letterSpacing: '0.16px',
        color: 'var(--app-text-primary)',
    },
    newSidebarDate: {
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '14px',
        letterSpacing: '0.16px',
        color: 'var(--app-text-muted)',
    },
    date: {
        fontSize: '12px',
        color: 'var(--app-text-muted)',
    },
    sidebarDate: {
        fontSize: '12px',
        color: 'var(--app-text-primary)',
    },
};

export default ProjectThumbnailCard;
